import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useShop } from '@/contexts/ShopContext';
import { api } from '@/lib/api';
import type { PublicOrder, PublicOrderStatus } from '@/types/commerce';

export interface PublicOrdersParams {
  status?: PublicOrderStatus;
  workflow?:
    | 'all'
    | 'new'
    | 'queued'
    | 'preparing'
    | 'ready'
    | 'out_for_delivery'
    | 'served'
    | 'refused';
  page_id?: string;
  search?: string;
  page?: number;
  per_page?: number;
}

export interface PublicOrdersMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  workflow_counts?: Record<
    'all' | 'new' | 'queued' | 'preparing' | 'ready' | 'out_for_delivery' | 'served' | 'refused',
    number
  >;
}

export function usePublicOrders(
  params: PublicOrdersParams = {},
  options: { enabled?: boolean; poll?: boolean } = {},
) {
  const { user } = useAuth();
  const { currentShop } = useShop();
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.workflow && params.workflow !== 'all') qs.set('workflow', params.workflow);
  if (params.page_id) qs.set('page_id', params.page_id);
  if (params.search) qs.set('search', params.search);
  if (params.page) qs.set('page', String(params.page));
  if (params.per_page) qs.set('per_page', String(params.per_page));
  const query = qs.toString();

  return useQuery({
    queryKey: ['public-orders', user?.id, currentShop?.id, params],
    enabled: Boolean(user?.id && currentShop?.id) && options.enabled !== false,
    refetchInterval: options.poll ? 15_000 : false,
    refetchIntervalInBackground: false,
    queryFn: async () => {
      const result = await api.getWithMeta<PublicOrder[]>(
        `/commerce/orders${query ? `?${query}` : ''}`,
      );
      return {
        data: result.body.data ?? [],
        meta: (result.body.meta ?? null) as PublicOrdersMeta | null,
      };
    },
    staleTime: 15_000,
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === user?.id && previousQuery?.queryKey[2] === currentShop?.id
        ? previous
        : undefined,
  });
}

export function usePublicOrder(id: string | undefined) {
  const { user } = useAuth();
  const { currentShop } = useShop();
  return useQuery({
    queryKey: ['public-orders', user?.id, currentShop?.id, 'detail', id],
    queryFn: () => api.get<PublicOrder>(`/commerce/orders/${id}`),
    enabled: !!id && Boolean(user?.id && currentShop?.id),
    staleTime: 10_000,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });
}
